import { RefCallback, RefObject, useEffect, useState } from 'react';

interface UseInfiniteScrollSentinelOptions {
  // Scroll container the sentinel lives in. Pass a ref; omit/null observes
  // against the browser viewport (IntersectionObserver default root). Omitting
  // it for a sentinel that lives inside a nested scroller is a bug even though
  // ancestor clipping makes it mostly work: when the scroller's height is
  // fractional Chrome rounds its clientHeight up, so at max scroll the last
  // element sits a sub-pixel below the clip box and never intersects — and
  // rootMargin can't compensate, because it grows the root box rather than the
  // intermediate clip.
  root?: RefObject<Element | null>;
  // When false no observer is attached — useful when sentinel isn't rendered yet.
  enabled?: boolean;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  // Fraction of the sentinel that must be visible to trigger (default 0 — any
  // pixel).
  threshold?: number;
  // Grows/shrinks the root box to prefetch before the sentinel is on screen,
  // e.g. '300px 0px 0px 0px' to fire 300px early at the top.
  rootMargin?: string;
}

// Attaches an IntersectionObserver to a sentinel element and fetches the next
// page whenever it scrolls into view. Returns a callback ref to attach to the
// sentinel node (typically a 1px spacer at the edge of a list). The sentinel is
// tracked in state (not a ref) so the observer also attaches when the node
// mounts after the effect has already run — e.g. inside a portal (Radix
// popover/dialog) whose content mounts a render later than the state change
// that enables the hook. The observer is recreated when the node or paging
// state changes and torn down on unmount or when disabled.
export const useInfiniteScrollSentinel = <T extends Element = HTMLDivElement>({
  root,
  enabled = true,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  threshold = 0,
  rootMargin,
}: UseInfiniteScrollSentinelOptions): RefCallback<T> => {
  const [sentinel, setSentinel] = useState<T | null>(null);

  useEffect(() => {
    if (!enabled || !sentinel || !hasNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: root?.current ?? null, threshold, rootMargin },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [
    root,
    enabled,
    sentinel,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    threshold,
    rootMargin,
  ]);

  return setSentinel;
};
